import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { usersApi, type AdminUserRow } from '@beacon/api-client';
import type { User } from '@beacon/core';
import type {
  UserCreateInput,
  UserUpdateInput,
} from '@beacon/core/validation';

import { beaconClient } from '@/lib/api/client.js';

export const userKeys = {
  all: ['users'] as const,
  assignable: () => [...userKeys.all, 'assignable'] as const,
  admin: () => [...userKeys.all, 'admin'] as const,
};

/** List of staff users available to assign tickets to.
 *
 *  Staff-only — the API 403s for client viewers. Pass `enabled: false`
 *  when mounted on a client surface (or in a component that may render
 *  for both, like the global ⌘K palette) so we don't fire a request
 *  that's destined to fail.
 */
export function useAssignableUsers(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: userKeys.assignable(),
    queryFn: async (): Promise<User[]> => {
      const res = await usersApi.listAssignable(beaconClient);
      return res.users;
    },
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: userKeys.admin(),
    queryFn: async (): Promise<AdminUserRow[]> => {
      const res = await usersApi.listAdmin(beaconClient);
      return res.users;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserCreateInput) =>
      usersApi.create(beaconClient, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: (err) => {
      toast.error("Couldn't create user", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}

interface UpdateVars {
  id: string;
  input: UserUpdateInput;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateVars) =>
      usersApi.update(beaconClient, id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: (err) => {
      toast.error("Couldn't update user", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}
